import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { ListChecks, Search, X } from 'lucide-react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import Badge from './ui/Badge';

const TYPE_LABELS = {
  number: 'Number',
  boolean: 'Yes / No',
  datetime: 'Date & time',
  text: 'Text',
};

// Lets a company pick which bank questions customers must answer before ordering a listing.
export default function QuestionPicker({ value, onChange }) {
  const [bank, setBank] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_API_URL}/api/questions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => setBank(res.data.questions))
      .catch(() => setBank([]))
      .finally(() => setLoading(false));
  }, []);

  // Focus search immediately so a growing bank never forces a scroll-and-hunt first
  useEffect(() => {
    if (modalOpen) searchInputRef.current?.focus();
    else setSearch('');
  }, [modalOpen]);

  const bankMap = useMemo(() => new Map(bank.map(q => [q.id, q])), [bank]);
  const selectedMap = useMemo(() => new Map(value.map(v => [v.question_id, v])), [value]);

  const sortedBank = useMemo(
    () => [...bank].sort((a, b) => a.label.localeCompare(b.label)),
    [bank]
  );

  const filteredBank = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedBank;
    return sortedBank.filter(item => item.label.toLowerCase().includes(q));
  }, [sortedBank, search]);

  const toggle = (questionId) => {
    if (selectedMap.has(questionId)) {
      onChange(value.filter(v => v.question_id !== questionId));
    } else {
      onChange([...value, { question_id: questionId, required: true }]);
    }
  };

  const setRequired = (questionId, required) => {
    onChange(value.map(v => (v.question_id === questionId ? { ...v, required } : v)));
  };

  return (
    <div className="flex flex-col gap-3">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map(v => {
            const q = bankMap.get(v.question_id);
            if (!q) return null;
            return (
              <span
                key={v.question_id}
                className="inline-flex items-start gap-1.5 max-w-full bg-sand-100 border border-sand-200 text-ink-700 text-xs pl-3 pr-2 py-1.5 rounded-2xl"
              >
                <span className="break-words">
                  {q.label}
                  {v.required && <span className="text-terra-400 font-semibold" title="Required"> *</span>}
                </span>
                <button
                  type="button"
                  onClick={() => toggle(v.question_id)}
                  className="text-ink-400 hover:text-uae-red transition-colors duration-200 shrink-0 mt-0.5"
                  aria-label={`Remove ${q.label}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={() => setModalOpen(true)}
        disabled={loading}
        className="group flex items-center gap-3 rounded-lg border border-dashed border-sand-300 bg-white text-left px-4 py-3 transition-all duration-200 hover:border-terra-400 hover:shadow-hover disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none"
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-terra-400/10 text-terra-400 shrink-0 transition-colors duration-200 group-hover:bg-terra-400 group-hover:text-white">
          <ListChecks className="w-4 h-4" />
        </span>
        <span className="text-sm font-medium text-ink-700 group-hover:text-terra-500">
          {loading ? 'Loading questions…' : value.length > 0 ? 'Edit questions' : 'Add questions from the bank'}
        </span>
      </button>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Select questions"
        footer={<Button variant="primary" onClick={() => setModalOpen(false)}>Done</Button>}
      >
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-ink-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search questions…"
              className="w-full pl-10 pr-9 py-2.5 rounded-full border border-sand-200 bg-sand-50 text-ink-900 text-sm focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-terra-400 focus:bg-white transition-all duration-200"
            />
            {search && (
              <button
                type="button"
                onClick={() => { setSearch(''); searchInputRef.current?.focus(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700 transition-colors duration-200"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {bank.length > 0 && (
            <p className="text-xs text-ink-400 -mt-1">
              {search ? `${filteredBank.length} of ${bank.length} questions` : `${bank.length} question${bank.length === 1 ? '' : 's'} available`}
            </p>
          )}

          <div className="flex flex-col gap-2 max-h-80 overflow-y-auto -mx-1 px-1 py-0.5">
            {filteredBank.length === 0 && (
              <p className="text-sm text-ink-400 text-center py-6">No questions found.</p>
            )}
            {filteredBank.map(q => {
              const selected = selectedMap.get(q.id);
              return (
                <div
                  key={q.id}
                  className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 transition-colors duration-200 ${
                    selected ? 'border-terra-400 bg-sand-100' : 'border-sand-200 bg-white hover:border-sand-300'
                  }`}
                >
                  <label className="flex items-start gap-3 min-w-0 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={!!selected}
                      onChange={() => toggle(q.id)}
                      className="w-4 h-4 accent-terra-400 shrink-0 mt-0.5"
                    />
                    <span className="min-w-0 flex flex-col gap-1">
                      <span className="text-sm text-ink-900 leading-snug break-words">{q.label}</span>
                      <Badge variant="category" className="w-fit">
                        {TYPE_LABELS[q.type] || q.type}{q.unit ? ` · ${q.unit}` : ''}
                      </Badge>
                    </span>
                  </label>

                  {selected && (
                    <label className="flex items-center gap-1.5 text-xs text-ink-700 shrink-0 cursor-pointer pt-0.5">
                      <input
                        type="checkbox"
                        checked={selected.required}
                        onChange={e => setRequired(q.id, e.target.checked)}
                        className="w-3.5 h-3.5 accent-terra-400"
                      />
                      Required
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    </div>
  );
}
